import React from 'react'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import MenuIcon from '@mui/icons-material/Menu'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined'
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toggleMode, toggleSidebar } from '../../store/uiShellSlice'
import { logout } from '../../store/authSlice'
import Clock from '../Clock'

export default function TopBar() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const admin = useSelector((s) => s.auth.admin)
  const mode = useSelector((s) => s.uiShell.mode)
  const collapsed = useSelector((s) => s.uiShell.sidebarCollapsed)
  const [anchor, setAnchor] = React.useState(null)

  const handleLogout = () => {
    setAnchor(null)
    dispatch(logout())
    navigate('/login', { replace: true })
  }

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={(theme) => {
        const isDark = theme.palette.mode === 'dark'
        return {
          borderBottom: isDark ? '1px solid rgba(240,198,106,0.18)' : '1px solid rgba(91,63,214,0.13)',
          bgcolor: isDark ? 'rgba(11,13,24,0.82)' : 'rgba(255,250,240,0.84)',
          background: isDark
            ? 'linear-gradient(90deg, rgba(15,18,31,0.94), rgba(22,16,35,0.88)), radial-gradient(circle at 84% 0%, rgba(240,198,106,0.12), transparent 30%)'
            : 'linear-gradient(90deg, rgba(255,250,240,0.96), rgba(249,240,220,0.88)), radial-gradient(circle at 84% 0%, rgba(91,63,214,0.12), transparent 30%)',
          backdropFilter: 'blur(18px)',
          boxShadow: isDark ? '0 16px 38px rgba(0,0,0,0.2)' : '0 16px 34px rgba(73,47,25,0.1)',
        }
      }}
    >
      <Toolbar variant="dense" sx={{ gap: 1, minHeight: 56 }}>
        <Tooltip title={collapsed ? 'Expand navigation' : 'Collapse navigation'}>
          <IconButton
            edge="start"
            onClick={() => dispatch(toggleSidebar())}
            aria-label="Toggle sidebar"
            sx={(theme) => ({
              border:
                theme.palette.mode === 'dark'
                  ? '1px solid rgba(240,198,106,0.24)'
                  : '1px solid rgba(91,63,214,0.18)',
              color: theme.palette.mode === 'dark' ? '#f7d88d' : 'primary.main',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(91,63,214,0.06)',
              '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(240,198,106,0.12)' : 'rgba(91,63,214,0.1)' },
            })}
          >
            {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
          </IconButton>
        </Tooltip>
        <Chip
          size="small"
          icon={<LocalFireDepartmentRoundedIcon />}
          label="Royal Flush Elite"
          variant="outlined"
          sx={(theme) => ({
            color: theme.palette.mode === 'dark' ? '#ffe5a8' : 'secondary.dark',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(240,198,106,0.44)' : 'rgba(91,63,214,0.18)',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(240,198,106,0.08)' : 'rgba(240,198,106,0.22)',
            boxShadow: theme.palette.mode === 'dark' ? 'inset 0 1px 0 rgba(255,255,255,0.08)' : 'inset 0 1px 0 rgba(255,255,255,0.82)',
          })}
        />
        <Box flexGrow={1} />
        <Clock />
        <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
          <IconButton
            onClick={() => dispatch(toggleMode())}
            aria-label="Toggle theme"
            sx={(theme) => ({
              color: theme.palette.mode === 'dark' ? '#f7d88d' : 'primary.main',
              '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(240,198,106,0.12)' : 'rgba(91,63,214,0.1)' },
            })}
          >
            {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </IconButton>
        </Tooltip>
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label="Account menu"
          sx={(theme) => ({
            color: theme.palette.mode === 'dark' ? '#fff6dc' : 'text.primary',
            '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(91,63,214,0.16)' : 'rgba(91,63,214,0.1)' },
          })}
        >
          <AccountCircleOutlinedIcon />
        </IconButton>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
          <MenuItem disabled>
            <Typography variant="body2">{admin?.email || 'Admin'}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setAnchor(null); navigate('/settings/security') }}>Session & security</MenuItem>
          <MenuItem onClick={handleLogout}>Log out</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  )
}
